import React, { useState, useEffect } from "react";
import {
    Banknote, Users, Package, HardHat, Plus, X,
    Phone, IndianRupee, Wallet, Calendar, ArrowRight
} from "lucide-react";
import api from "../api/axiosInstance";

const SiteDetailCenter = ({ projectId, projectName, onBack }) => {
    const [activeTab, setActiveTab] = useState("Finance");
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);

    // Modals Management
    const [contractorModal, setContractorModal] = useState(false);
    const [paymentModal, setPaymentModal] = useState(false);
    const [selectedSub, setSelectedSub] = useState(null);

    const [selectedDate] = useState(new Date().toISOString().split("T")[0]);

    // Form State Containers
    const [subForm, setSubForm] = useState({ name: '', category: 'Carpenter', contact_no: '', total_contract_value: '' });
    const [payForm, setPayForm] = useState({ amount: '', method: 'UPI', remarks: '' });

    useEffect(() => {
        if (projectId) fetchSiteIntelligence();
    }, [projectId]);

    const fetchSiteIntelligence = async () => {
        setLoading(true);
        try {
            const res = await api.get(`get_site_intelligence.php?project_id=${projectId}&date=${selectedDate}`);
            setData(res.data.details);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const saveContractor = async (e) => {
        e.preventDefault();
        if (!subForm.name || !subForm.total_contract_value) return alert("Fill required parameters");
        try {
            await api.post("save_sub_contractor.php", { ...subForm, project_id: projectId });
            setContractorModal(false);
            setSubForm({ name: '', category: 'Carpenter', contact_no: '', total_contract_value: '' });
            fetchSiteIntelligence();
        } catch (err) { alert("Error updating registry"); }
    };

    const saveContractorPayment = async (e) => {
        e.preventDefault();
        if (!payForm.amount) return alert("Provide payment allocation amount");
        try {
            await api.post("save_sub_payment.php", {
                ...payForm,
                sub_contractor_id: selectedSub.id,
                date: new Date().toISOString().split("T")[0]
            });
            setPaymentModal(false);
            setPayForm({ amount: '', method: 'UPI', remarks: '' });
            fetchSiteIntelligence();
        } catch (err) { alert("Disbursement processing failed"); }
    };

    if (loading) return <div className="p-20 text-center font-black animate-pulse text-slate-400 uppercase tracking-widest">Syncing Matrix...</div>;

    const tabs = ["Finance", "Workforce", "Materials", "Contractors"];

    return (
        <div className="space-y-8 animate-in fade-in duration-300">
            {/* Dynamic Dashboard Section Header */}
            <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 bg-white p-8 rounded-[36px] border border-slate-100 shadow-sm">
                <div>
                    <h2 className="text-3xl font-black italic uppercase text-slate-900 tracking-tighter">{projectName}</h2>
                    <p className="text-[10px] font-black uppercase text-indigo-500 tracking-widest mt-1">Cross-Operational Site Intelligence</p>
                </div>
                <div className="flex bg-slate-100 p-1 rounded-2xl self-start sm:self-auto">
                    {tabs.map(tab => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === tab ? "bg-white text-slate-900 shadow-sm" : "text-slate-400 hover:text-slate-600"
                                }`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>
            </div>

            {/* Display Grid Tabs Layout */}
            <div className="min-h-[450px]">
                {/* TAB A: FINANCIAL LEDGER ACCRUALS */}
                {activeTab === "Finance" && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="bg-white p-8 rounded-[40px] border border-slate-100 shadow-sm space-y-6">
                            <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                                <Banknote size={16} className="text-indigo-500" /> Client Capital Accumulation
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-slate-50 p-5 rounded-2xl">
                                    <p className="text-[9px] font-black text-slate-400 uppercase">Total Volume Invoiced</p>
                                    <p className="text-xl font-black text-slate-900 mt-1">₹{parseFloat(data.finance.client_invoiced || 0).toLocaleString()}</p>
                                </div>
                                <div className="bg-emerald-50/40 p-5 rounded-2xl">
                                    <p className="text-[9px] font-black text-emerald-600 uppercase">Liquid Reserves Received</p>
                                    <p className="text-xl font-black text-emerald-600 mt-1">₹{parseFloat(data.finance.client_received || 0).toLocaleString()}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-8 rounded-[40px] border border-slate-100 shadow-sm space-y-6">
                            <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                                <Wallet size={16} className="text-amber-500" /> Supply & Trade Liabilities
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-slate-50 p-5 rounded-2xl">
                                    <p className="text-[9px] font-black text-slate-400 uppercase">Aggregated Vendor Bills</p>
                                    <p className="text-xl font-black text-slate-900 mt-1">₹{parseFloat(data.finance.vendor_invoiced || 0).toLocaleString()}</p>
                                </div>
                                <div className="bg-indigo-50/40 p-5 rounded-2xl">
                                    <p className="text-[9px] font-black text-indigo-600 uppercase">Settled Capital Outflows</p>
                                    <p className="text-xl font-black text-indigo-600 mt-1">₹{parseFloat(data.finance.vendor_paid || 0).toLocaleString()}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* TAB B: HUMAN WORKFORCE REGISTRY */}
                {activeTab === "Workforce" && (
                    <div className="bg-white p-8 rounded-[40px] border border-slate-100 shadow-sm max-w-2xl">
                        <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-6 flex items-center gap-2">
                            <Calendar size={16} /> Personnel Attendance Manifest: {selectedDate}
                        </h3>
                        <div className="divide-y divide-slate-50">
                            {data.workforce.map((emp, idx) => (
                                <div key={idx} className="flex items-center justify-between py-4">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2.5 bg-slate-50 rounded-xl text-slate-600"><Users size={16} /></div>
                                        <span className="text-sm font-black text-slate-800 uppercase">{emp.name}</span>
                                    </div>
                                    <span className={`text-xs font-black uppercase tracking-widest ${emp.status === 'Present' ? 'text-emerald-500' : 'text-rose-500'}`}>
                                        {emp.status}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* TAB C: IN-SITU LOGISTICS MONITOR */}
                {activeTab === "Materials" && (
                    <div className="bg-white p-8 rounded-[40px] border border-slate-100 shadow-sm max-w-4xl">
                        <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-6">Supervisor Requisition Stream</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {data.material_requests.map((req, i) => (
                                <div key={i} className="p-5 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between">
                                    <div>
                                        <h4 className="font-black text-slate-800 uppercase text-xs">{req.item_name}</h4>
                                        <p className="text-[10px] font-bold text-slate-400 uppercase mt-0.5">{req.qty} Units • Logged: {req.date}</p>
                                    </div>
                                    <span className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-wider ${req.status === 'Pending' ? 'bg-amber-100 text-amber-600' : 'bg-emerald-100 text-emerald-600'
                                        }`}>{req.status}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* TAB D: MANUAL TRADE SUB-CONTRACTORS AGGREGATE */}
                {activeTab === "Contractors" && (
                    <div className="space-y-6">
                        <div className="flex justify-between items-center">
                            <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Active Specialized Trade Agreements</p>
                            <button
                                onClick={() => setContractorModal(true)}
                                className="bg-slate-900 text-white px-5 py-2 rounded-xl text-[10px] font-black uppercase flex items-center gap-2 tracking-widest hover:bg-indigo-600 transition-all"
                            >
                                <Plus size={14} /> Incorporate Contractor
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {data.sub_contractors.map((sub, i) => {
                                const due = sub.contract_value - sub.paid;
                                return (
                                    <div key={i} className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex flex-col justify-between space-y-4">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <h4 className="font-black text-slate-900 text-sm uppercase italic">{sub.name}</h4>
                                                <span className="text-[9px] font-black px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded-md uppercase tracking-wider mt-1 inline-block">{sub.category}</span>
                                            </div>
                                            <button
                                                onClick={() => { setSelectedSub(sub); setPaymentModal(true); }}
                                                className="px-3 py-1 bg-emerald-50 hover:bg-emerald-600 text-emerald-600 hover:text-white rounded-lg text-[10px] font-black uppercase tracking-wider transition-all"
                                            >
                                                Pay
                                            </button>
                                        </div>

                                        <div className="grid grid-cols-2 gap-2 text-xs pt-2 border-t border-slate-50">
                                            <div>
                                                <p className="text-[9px] font-bold text-slate-400 uppercase">Valuation</p>
                                                <p className="font-black text-slate-800"> can check: ₹{parseFloat(sub.contract_value).toLocaleString()}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-[9px] font-bold text-slate-400 uppercase">Outstanding</p>
                                                <p className="font-black text-rose-500">₹{due.toLocaleString()}</p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* MODAL I: SAVE SUB-CONTRACTOR */}
            {contractorModal && (
                <div className="fixed inset-0 bg-slate-950/30 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white p-8 rounded-[40px] border border-slate-100 max-w-md w-full shadow-2xl animate-in zoom-in-95 duration-150">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-black italic uppercase tracking-tighter">Register Specialized Trade</h3>
                            <button onClick={() => setContractorModal(false)}><X className="text-slate-400" size={18} /></button>
                        </div>
                        <form onSubmit={saveContractor} className="space-y-4">
                            <input type="text" placeholder="Trade Group / Contractor Name" className="w-full bg-slate-50 p-4 rounded-xl text-sm font-bold border border-slate-100 outline-none focus:ring-2 ring-indigo-500" value={subForm.name} onChange={(e) => setSubForm({ ...subForm, name: e.target.value })} />
                            <select className="w-full bg-slate-50 p-4 rounded-xl text-sm font-bold border border-slate-100 outline-none" value={subForm.category} onChange={(e) => setSubForm({ ...subForm, category: e.target.value })}>
                                {["Carpenter", "Plumber", "Electrician", "Painter", "Flooring", "Other"].map(cat => <option key={cat} value={cat}>{cat}</option>)}
                            </select>
                            <input type="text" placeholder="Contact Allocation Number" className="w-full bg-slate-50 p-4 rounded-xl text-sm font-bold border border-slate-100 outline-none" value={subForm.contact_no} onChange={(e) => setSubForm({ ...subForm, contact_no: e.target.value })} />
                            <input type="number" placeholder="Contract Cap Valuation (₹)" className="w-full bg-slate-50 p-4 rounded-xl text-sm font-bold border border-slate-100 outline-none" value={subForm.total_contract_value} onChange={(e) => setSubForm({ ...subForm, total_contract_value: e.target.value })} />
                            <button type="submit" className="w-full py-4 bg-slate-900 hover:bg-indigo-600 text-white font-black uppercase text-xs rounded-xl tracking-widest transition-all">Commit Registry Entry</button>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL II: REMIT PAYMENT EXECUTION */}
            {paymentModal && (
                <div className="fixed inset-0 bg-slate-950/30 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white p-8 rounded-[40px] border border-slate-100 max-w-md w-full shadow-2xl animate-in zoom-in-95 duration-150">
                        <div className="flex justify-between items-center mb-6">
                            <div>
                                <h3 className="text-lg font-black italic uppercase tracking-tighter">Execute Outflow Settlement</h3>
                                <p className="text-[10px] text-slate-400 font-bold uppercase mt-0.5">Allocation for: {selectedSub?.name}</p>
                            </div>
                            <button onClick={() => setPaymentModal(false)}><X className="text-slate-400" size={18} /></button>
                        </div>
                        <form onSubmit={saveContractorPayment} className="space-y-4">
                            <input type="number" placeholder="Disbursement Quantum Amount (₹)" className="w-full bg-slate-50 p-4 rounded-xl text-sm font-bold border border-slate-100 outline-none" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} />
                            <select className="w-full bg-slate-50 p-4 rounded-xl text-sm font-bold border border-slate-100 outline-none" value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })}>
                                {["UPI", "Cash", "Bank Transfer", "Cheque"].map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                            <input type="text" placeholder="Transaction Manifest Notes / Remarks" className="w-full bg-slate-50 p-4 rounded-xl text-sm font-bold border border-slate-100 outline-none" value={payForm.remarks} onChange={(e) => setPayForm({ ...payForm, remarks: e.target.value })} />
                            <button type="submit" className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-xs rounded-xl tracking-widest transition-all">Authorize Disbursal</button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SiteDetailCenter;